import {
  Box,
  Button,
  HStack,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Text,
  VStack,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import screen from "../login/images/loginscreen.svg";
import { FaUserAlt } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { postData } from "../../redux/actions/common.action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  ArrowLeft,
  LogIn,
  Send,
  Shield,
} from "lucide-react";

interface FormData {
  Email: string;
  Password: string;
  type: string;
}

interface FormData2 {
  Email: string;
  type: string;
}

const Login: React.FC = () => {
  interface Loading {
    loading: boolean;
  }

  const { loading } = useSelector((state: { common: Loading }) => state.common);
  const [show, setShow] = useState<boolean>(false);
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);

  const dispatch: any = useDispatch();
  const endpoint = "auth/signin";
  const endpoint2 = "auth/request-forgot-password";

  const navigate = useNavigate();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      Email: "",
      Password: "",
      type: "",
    },
  });

  const {
    register: register2,
    handleSubmit: handleSubmit2,
    reset: reset2,
    formState: { errors: errors2 },
  } = useForm<FormData2>();

  const onSubmit = (data: FormData) => {
    dispatch(postData({ ...data, type: "web" }, endpoint, navigate, toast));
  };

  const onSubmit2 = (data: FormData2) => {
    dispatch(
      postData({ ...data, type: "web" }, endpoint2, navigate, toast),
    ).then(() => {
      setForgotPassword(false);
      reset2();
    });
  };

  useEffect(() => {
    const token = Cookies.get("token");
    // If we are not loading, and a token exists, the user is logged in.
    // Send them to the dashboard.
    if (!loading && token) {
      navigate("/");
    }
  }, [loading, navigate]);

  const handleForgotPasswordClick = () => setForgotPassword(true);
  const handleGoBackClick = () => {
    setForgotPassword(false);
    reset({ Email: "", Password: "", type: "" });
  };

  useEffect(() => {
    const logoutMessage = Cookies.get("logoutMessage");
    if (logoutMessage) {
      toast({
        title: logoutMessage,
        status: "error",
        duration: 3000,
        position: "top-right",
        isClosable: true,
      });

      setTimeout(() => {
        Cookies.remove("logoutMessage");
      }, 1000);
    }
  }, []);

  const MotionBox = motion(Box);

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
      position="relative"
      overflow="hidden"
    >
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33% { transform: translate(30px, -30px) rotate(5deg); }
            66% { transform: translate(-20px, 20px) rotate(-5deg); }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}
      </style>

      {/* Animated Background Blobs */}
      <MotionBox
        position="absolute"
        top="-100px"
        left="-100px"
        w="400px"
        h="400px"
        bgGradient="linear(to-br, blue.300, purple.400)"
        filter="blur(80px)"
        opacity={0.3}
        borderRadius="full"
        animate={{
          x: [0, 50, 0, -50, 0],
          y: [0, -30, 0, 30, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: "mirror",
          ease: "easeInOut",
        }}
      />

      <MotionBox
        position="absolute"
        bottom="-100px"
        right="-100px"
        w="400px"
        h="400px"
        bgGradient="linear(to-br, purple.300, pink.400)"
        filter="blur(80px)"
        opacity={0.3}
        borderRadius="full"
        animate={{
          x: [0, -40, 0, 40, 0],
          y: [0, 20, 0, -20, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          repeatType: "mirror",
          ease: "easeInOut",
        }}
      />

      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        w="95%"
        maxW="1100px"
        bg="white"
        borderRadius="3xl"
        overflow="hidden"
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
        border="1px"
        borderColor="gray.100"
      >
        {/* Left Illustration */}
        <Box
          display={{ base: "none", lg: "flex" }}
          position="relative"
          bgGradient="linear(to-br, blue.500, purple.600)"
          alignItems="center"
          justifyContent="center"
          p={12}
          overflow="hidden"
        >
          {/* Decorative circles */}
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            w="200px"
            h="200px"
            bg="whiteAlpha.100"
            borderRadius="full"
            style={{ animation: "float 15s infinite ease-in-out" }}
          />
          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            w="150px"
            h="150px"
            bg="whiteAlpha.100"
            borderRadius="full"
            style={{ animation: "float 20s infinite ease-in-out reverse" }}
          />

          <VStack
            spacing={6}
            zIndex={1}
            style={{ animation: "fadeInUp 0.8s ease-out" }}
          >
            <Image src={screen} alt="Login illustration" w="85%" maxW="400px" />
            <VStack spacing={2} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color="white">
                Welcome to Matrix CRM
              </Text>
              <Text fontSize="sm" color="whiteAlpha.900" maxW="300px">
                Manage your business operations efficiently with our powerful
                CRM platform
              </Text>
            </VStack>
          </VStack>
        </Box>

        {/* Right Form Section */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={{ base: 6, md: 12 }}
        >
          <VStack
            spacing={6}
            w="full"
            maxW="400px"
            style={{ animation: "slideInRight 0.8s ease-out" }}
          >
            {/* Header */}
            <VStack spacing={2} w="full">
              <Box
                p={4}
                bgGradient="linear(to-br, blue.500, purple.600)"
                borderRadius="2xl"
                boxShadow="lg"
              >
                <Shield size={32} color="white" />
              </Box>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {forgotPassword ? "Reset Password" : "Sign In"}
              </Text>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                {forgotPassword
                  ? "Enter your email to receive reset instructions"
                  : "Enter your credentials to access your account"}
              </Text>
            </VStack>

            <AnimatePresence mode="wait">
              {!forgotPassword ? (
                <motion.form
                  key="login-form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handleSubmit(onSubmit)}
                  style={{ width: "100%" }}
                >
                  <VStack spacing={5} w="full">
                    {/* Email Input */}
                    <Box w="full">
                      <HStack spacing={2} mb={2}>
                        <Mail size={16} color="#4299E1" />
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.700"
                        >
                          Email Address
                        </Text>
                      </HStack>
                      <InputGroup>
                        <Input
                          {...register("Email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address",
                            },
                          })}
                          placeholder="your@email.com"
                          size="lg"
                          borderRadius="lg"
                          border="1px"
                          borderColor={errors.Email ? "red.500" : "gray.300"}
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)",
                          }}
                          autoComplete="email"
                        />
                        <InputRightElement h="full">
                          <FaUserAlt color="#4299E1" size={16} />
                        </InputRightElement>
                      </InputGroup>
                      {errors.Email && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                          {errors.Email.message}
                        </Text>
                      )}
                    </Box>

                    {/* Password Input */}
                    <Box w="full">
                      <HStack spacing={2} mb={2}>
                        <Lock size={16} color="#4299E1" />
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.700"
                        >
                          Password
                        </Text>
                      </HStack>
                      <InputGroup>
                        <Input
                          {...register("Password", {
                            required: "Password is required",
                          })}
                          type={show ? "text" : "password"}
                          placeholder="Enter your password"
                          size="lg"
                          borderRadius="lg"
                          border="1px"
                          borderColor={errors.Password ? "red.500" : "gray.300"}
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)",
                          }}
                          autoComplete="current-password"
                        />
                        <InputRightElement h="full">
                          <IconButton
                            aria-label={
                              show ? "Hide password" : "Show password"
                            }
                            icon={
                              show ? <EyeOff size={16} /> : <Eye size={16} />
                            }
                            size="sm"
                            variant="ghost"
                            onClick={() => setShow(!show)}
                            color="gray.500"
                            _hover={{ bg: "transparent", color: "blue.500" }}
                          />
                        </InputRightElement>
                      </InputGroup>
                      {errors.Password && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                          {errors.Password.message}
                        </Text>
                      )}
                    </Box>

                    {/* Forgot Password Link */}
                    <HStack w="full" justify="flex-end">
                      <Button
                        variant="link"
                        size="sm"
                        color="blue.500"
                        fontWeight="medium"
                        onClick={handleForgotPasswordClick}
                        _hover={{ color: "blue.600" }}
                      >
                        Forgot password?
                      </Button>
                    </HStack>

                    {/* Login Button */}
                    <Button
                      type="submit"
                      w="full"
                      size="lg"
                      bgGradient="linear(to-r, blue.500, purple.600)"
                      color="white"
                      fontWeight="bold"
                      borderRadius="lg"
                      leftIcon={<LogIn size={20} />}
                      _hover={{
                        bgGradient: "linear(to-r, blue.600, purple.700)",
                        transform: "translateY(-2px)",
                        boxShadow: "lg",
                      }}
                      _active={{
                        transform: "translateY(0)",
                      }}
                      transition="all 0.2s"
                      isLoading={loading}
                      loadingText="Signing in..."
                    >
                      Sign In
                    </Button>
                  </VStack>
                </motion.form>
              ) : (
                <motion.form
                  key="forgot-password-form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handleSubmit2(onSubmit2)}
                  style={{ width: "100%" }}
                >
                  <VStack spacing={5} w="full">
                    {/* Email Input */}
                    <Box w="full">
                      <HStack spacing={2} mb={2}>
                        <Mail size={16} color="#4299E1" />
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.700"
                        >
                          Email Address
                        </Text>
                      </HStack>
                      <Input
                        {...register2("Email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                        placeholder="your@email.com"
                        size="lg"
                        borderRadius="lg"
                        border="1px"
                        borderColor={errors2.Email ? "red.500" : "gray.300"}
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)",
                        }}
                        autoComplete="email"
                      />
                      {errors2.Email && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                          {errors2.Email.message}
                        </Text>
                      )}
                    </Box>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      w="full"
                      size="lg"
                      bgGradient="linear(to-r, blue.500, purple.600)"
                      color="white"
                      fontWeight="bold"
                      borderRadius="lg"
                      leftIcon={<Send size={20} />}
                      _hover={{
                        bgGradient: "linear(to-r, blue.600, purple.700)",
                        transform: "translateY(-2px)",
                        boxShadow: "lg",
                      }}
                      _active={{
                        transform: "translateY(0)",
                      }}
                      transition="all 0.2s"
                      isLoading={loading}
                      loadingText="Sending..."
                    >
                      Send Reset Link
                    </Button>

                    {/* Back Button */}
                    <Button
                      variant="ghost"
                      w="full"
                      leftIcon={<ArrowLeft size={16} />}
                      onClick={handleGoBackClick}
                      color="gray.600"
                      _hover={{ bg: "gray.100" }}
                    >
                      Back to Sign In
                    </Button>
                  </VStack>
                </motion.form>
              )}
            </AnimatePresence>

            {/* Footer Text */}
            <Box pt={4}>
              <Text fontSize="xs" color="gray.500" textAlign="center">
                © 2025 Matrix CRM. All rights reserved.
              </Text>
            </Box>
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default Login;
